import React, { useState } from 'react';
import { Button } from '../Button';
import { Zap, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../../hooks/usePayment';
import { PRICES, STARS_AMOUNT, calculateSavings, type Period } from '../../constants/pricing';
import { useUserStore } from '../../stores/useUserStore';
import { PeriodSelector } from './PeriodSelector';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { gradientTextStyle } from '../../lib/gradients';

type PaymentMethod = 'stars' | 'crypto';

interface SubscriptionCardProps {
  onClose?: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { reloadSubscription } = useUserStore();
  const [period, setPeriod] = useState<Period>('3');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('stars');

  const { isProcessing, handleSubscribe } = usePayment({
    period,
    paymentMethod,
    onSuccess: () => {
      reloadSubscription();
      if (onClose) {
        onClose();
      }
    }
  });

  const currentPrice = PRICES[period];
  const originalPrice = +(9.99 * parseInt(period)).toFixed(2);
  const savings = calculateSavings(period);

  const features = [
    t('PROFILE_SCREEN.FEATURES.EARLY_ACCESS'),
    t('PROFILE_SCREEN.FEATURES.EXCLUSIVE_DEFI'),
    t('PROFILE_SCREEN.FEATURES.NOTIFICATIONS'),
    t('PROFILE_SCREEN.FEATURES.GUIDES'),
    t('PROFILE_SCREEN.FEATURES.INSIGHTS')
  ];

  const handleToggle = (newPeriod: Period) => {
    setPeriod(newPeriod);
  };

  const handlePaymentMethodToggle = (method: PaymentMethod) => {
    setPaymentMethod(method);
  };

  return (
    <div className="flex flex-col gap-4 p-5 max-[640px]:p-4 max-[360px]:p-3 max-[360px]:gap-3">
      <div className="flex justify-between items-start relative z-[2] gap-3">
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <h2 
            className="text-xl font-semibold text-tg-text m-0 tracking-[-0.02em] leading-tight max-[640px]:text-lg max-[360px]:text-base"
            style={gradientTextStyle}
          >
            {t('PROFILE_SCREEN.PREMIUM.TITLE')}
          </h2>
          <p className="text-sm text-tg-hint m-0 max-[640px]:text-xs">{t('PROFILE_SCREEN.PREMIUM.CAPTION')}</p>
        </div>
        {onClose && (
          <button 
            onClick={onClose} 
            className="p-1.5 bg-transparent border-none cursor-pointer text-tg-hint hover:text-tg-text transition-colors duration-200 active:opacity-70 flex-shrink-0"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1.5 relative z-[2]">
        <PeriodSelector
          period={period}
          onPeriodChange={handleToggle}
          showBestValue={true}
          size="compact"
        />
      </div>

      <div className="text-center p-4 bg-tg-secondary-bg rounded-xl relative z-[2] border border-pink-500/10 flex flex-col items-center gap-2 max-[640px]:p-3 max-[640px]:gap-1.5">
        <div className="flex items-baseline justify-center gap-2 flex-wrap">
          {period !== '3' && (
            <span className="text-lg font-medium text-tg-hint line-through max-[640px]:text-base">${originalPrice}</span>
          )}
          <span 
            className="text-3xl font-bold max-[640px]:text-2xl max-[360px]:text-xl"
            style={{
              ...gradientTextStyle,
              letterSpacing: '-0.5px'
            }}
          >
            ${currentPrice}
          </span>
          <span className="text-sm text-tg-hint font-medium max-[640px]:text-xs">/{period} {t('PROFILE_SCREEN.PREMIUM.MONTHS')}</span>
        </div>
        {paymentMethod === 'stars' ? (
          <>
            <div className="inline-flex items-center justify-center gap-1.5 text-base font-semibold text-tg-text px-4 py-2 bg-tg-secondary-bg rounded-lg border border-pink-500/10 shadow-[0_2px_8px_rgba(0,0,0,0.1)] max-[640px]:text-sm max-[640px]:px-3 max-[640px]:py-1.5">
              <span>⭐</span>
              <span>{STARS_AMOUNT[period]} Stars</span>
            </div>
            {period !== '3' && (
              <div className="inline-flex items-center justify-center gap-1.5 text-sm text-[#FF0080] font-semibold px-3 py-1.5 rounded-lg border border-pink-500/10 max-[640px]:text-xs max-[640px]:px-2.5 max-[640px]:py-1">
                <Zap size={14} className="max-[640px]:w-3 max-[640px]:h-3" />
                {t('PROFILE_SCREEN.PREMIUM.YOU_SAVE')} ${savings}
              </div>
            )}
          </>
        ) : period !== '3' && (
          <div className="inline-flex items-center justify-center gap-1.5 text-sm text-[#FF0080] font-semibold px-3 py-1.5 rounded-lg border border-pink-500/10 max-[640px]:text-xs max-[640px]:px-2.5 max-[640px]:py-1">
            <Zap size={14} className="max-[640px]:w-3 max-[640px]:h-3" />
            {t('PROFILE_SCREEN.PREMIUM.YOU_SAVE')} ${savings}
          </div>
        )}
      </div>

      <h2 className="text-lg font-semibold text-tg-text m-0 mb-2 relative z-[2] max-[640px]:text-base max-[640px]:mb-1.5">{t('PROFILE_SCREEN.PREMIUM.PAYMENT_METHOD')}</h2>

      <div className="relative z-[2]">
        <PaymentMethodSelector
          paymentMethod={paymentMethod}
          onPaymentMethodChange={handlePaymentMethodToggle}
        />
      </div>

      <div className="flex flex-col gap-2 relative z-[2] max-[640px]:gap-1.5">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start gap-2 text-sm text-tg-text max-[640px]:text-xs max-[640px]:gap-1.5">
            <Check className="flex-shrink-0 text-[#FF0080] mt-0.5 max-[640px]:mt-0 max-[640px]:w-3.5 max-[640px]:h-3.5" size={16} />
            <span className="leading-relaxed">{feature}</span>
          </div>
        ))}
      </div>

      <Button 
        onClick={handleSubscribe}
        fullWidth
        disabled={isProcessing}
        className="mt-2 max-[640px]:mt-1.5"
      >
        {isProcessing 
          ? t('PROFILE_SCREEN.PREMIUM.PROCESSING')
          : paymentMethod === 'stars' 
            ? t('PROFILE_SCREEN.PREMIUM.PAY_STARS', { amount: STARS_AMOUNT[period] })
            : t('PROFILE_SCREEN.PREMIUM.PAY_CRYPTO', { amount: currentPrice })}
      </Button>
    </div>
  );
};
